import { useTranslation } from "react-i18next";

import type { PublicPlanCard } from "@/api/queries/publicPlans";
import { pickI18nBlob } from "@/components/admin/I18nField";

import {
  formatCompact,
  formatMoney,
  formatMoneyRange,
  formatPercent,
  freeMonths,
  moneyParts,
  monthlyMinor,
  SectionHead,
} from "./pricingFormat";

/**
 * Volumes the cap chart samples, as multiples of the volume at which the
 * commission reaches its ceiling. Chart geometry, not pricing: the numbers
 * printed under the bars all come from the plan — five below the break
 * point so the growth reads, the break point itself, and two past it so
 * the flat line reads.
 */
const BAR_STEPS = [0.125, 0.25, 0.375, 0.625, 0.875, 1, 2, 3.75] as const;

/** Height in px of a bar whose commission sits exactly on the ceiling. */
const CEILING_PX = 96;

/**
 * The tier the explainer illustrates, or ``undefined`` when no tier has a
 * subscription, a rate and a ceiling — the section then does not render.
 * Exported so the page knows whether the VAT sentence, printed once inside
 * the explainer (KAN-1948 #53), has to go somewhere else.
 */
export function explainedPlan(plans: PublicPlanCard[]): PublicPlanCard | undefined {
  const candidates = plans.filter(
    (p) =>
      (p.price_monthly_cents ?? 0) > 0 &&
      p.platform_fee_bps > 0 &&
      p.platform_fee_cap_monthly_minor != null &&
      // Adding a ceiling in another currency to the subscription would
      // print a confident, wrong total — same rule as ``planCost``.
      p.platform_fee_cap_currency === p.currency,
  );
  return candidates.find((p) => p.is_recommended) ?? candidates[0];
}

/**
 * „Paušál, procento, strop" — the three numbers a tier is priced by, and
 * a chart of the commission flattening out at its ceiling.
 *
 * Illustrated on ONE tier, because a formula with three plans' numbers in
 * it reads as noise: the one the admin marks recommended, else the first
 * that has all three numbers. A tier without a subscription, a rate or a
 * ceiling cannot illustrate the formula, and a payload with no such tier
 * renders no section rather than a formula with blanks.
 */
export function FeeExplainer({
  plans,
  billing,
  lang,
  vatNote,
}: {
  plans: PublicPlanCard[];
  /** KAN-1947 — the formula follows the toggle, like the cards above it. */
  billing: "monthly" | "annual";
  lang: string;
  /** Věta o DPH odvozená z Profilu firmy provozovatele (KAN-1806). */
  vatNote: string;
}): JSX.Element | null {
  const { t } = useTranslation("saas");

  const plan = explainedPlan(plans);
  if (!plan) return null;

  const currency = plan.currency;
  const annual = billing === "annual" && plan.price_annual_cents != null;
  const subscription = annual ? monthlyMinor(plan, "annual")! : plan.price_monthly_cents!;
  const cap = plan.platform_fee_cap_monthly_minor!;
  const bps = plan.platform_fee_bps;
  const bindsAt = (cap * 10_000) / bps;
  const months = freeMonths(plan);

  const bars = BAR_STEPS.map((step) => {
    const volume = bindsAt * step;
    const uncapped = (volume * bps) / 10_000;
    const fee = Math.min(uncapped, cap);
    return { volume, fee, hit: uncapped >= cap };
  });

  // The magnitude word ("tis.", "mil.") is printed on the first and last
  // label and wherever it changes — once per run, as the design has it.
  let previousSuffix = "";
  const axis = bars.map((bar, i) => {
    const { suffix } = formatCompact(bar.volume, currency, lang);
    const show = i === 0 || i === bars.length - 1 || suffix !== previousSuffix;
    previousSuffix = suffix;
    return formatCompact(bar.volume, currency, lang, show).text;
  });

  const planName = pickI18nBlob(plan.name_i18n, plan.name, plan.name_en, lang);

  return (
    <section className="band alt tight" data-testid="pricing-plans-fee-explainer">
      <div className="wrap">
        <SectionHead
          eyebrow={t("pricingPlans.explainer.eyebrow")}
          heading={t("pricingPlans.explainer.heading")}
          lead={t("pricingPlans.explainer.lead")}
        />
        <div className="formula">
          <div className="fbox">
            <div className="fl">{t("pricingPlans.explainer.subscription.label")}</div>
            <div className="fv" data-testid="pricing-explainer-subscription">
              {formatMoney(subscription, currency, lang)}
            </div>
            <p className="fn">
              {annual ? (
                t("pricingPlans.explainer.subscription.noteYearly", {
                  total: formatMoney(plan.price_annual_cents!, currency, lang),
                })
              ) : (
                <>
                  {t("pricingPlans.explainer.subscription.note")}
                  {months > 0 &&
                    ` ${t("pricingPlans.explainer.subscription.noteAnnual", { count: months })}`}
                </>
              )}
            </p>
          </div>
          <div className="op" aria-hidden="true">+</div>
          <div className="fbox">
            <div className="fl">{t("pricingPlans.explainer.fee.label")}</div>
            <div className="fv">
              {t("pricingPlans.explainer.fee.value", {
                percent: formatPercent(bps, lang),
              })}
            </div>
            <p className="fn">{t("pricingPlans.explainer.fee.note")}</p>
          </div>
          <div className="op" aria-hidden="true">≤</div>
          <div className="fbox">
            <div className="fl">{t("pricingPlans.explainer.cap.label")}</div>
            <div className="fv">{formatMoney(cap, currency, lang)}</div>
            <p className="fn">{t("pricingPlans.explainer.cap.note")}</p>
          </div>
          <div className="op" aria-hidden="true">=</div>
          <div className="fbox out">
            <div className="fl">{t("pricingPlans.explainer.total.label")}</div>
            <div className="fv" data-testid="pricing-explainer-total">
              {formatMoneyRange(subscription, subscription + cap, currency, lang)}
            </div>
            <p className="fn">{t("pricingPlans.explainer.total.note", { vat: vatNote })}</p>
          </div>
        </div>

        <div className="capchart">
          <div className="caphead">
            <span>{t("pricingPlans.explainer.chartTitle", { plan: planName })}</span>
            <span>{t("pricingPlans.explainer.chartAxis")}</span>
          </div>
          <div className="bars" aria-hidden="true">
            <div className="ceil">
              <span>
                {t("pricingPlans.explainer.ceiling", {
                  cap: formatMoney(cap, currency, lang),
                })}
              </span>
            </div>
            {bars.map((bar, i) => {
              const ratio = bar.fee / cap;
              return (
                <div
                  key={i}
                  className={
                    bar.hit ? "bar hit" : ratio > 0.8 ? "bar inl" : "bar"
                  }
                  style={{ height: Math.round(CEILING_PX * ratio) }}
                  data-v={moneyParts(Math.round(bar.fee), currency, lang).amount}
                />
              );
            })}
          </div>
          <div className="barx" aria-hidden="true">
            {axis.map((label, i) => (
              <span key={i}>{label}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
